import axios from 'axios';
import { API_URL } from '../constantes/constantes';

/**
 * Servicio para generar tokens de autenticación de sesión
 * 
 * @description Solicita un token de autenticación al servidor usando un ID de sesión.
 * El token generado se utiliza para autenticar las peticiones de búsqueda y otros
 * servicios protegidos. Maneja automáticamente diferentes códigos de respuesta
 * y errores de red.
 * 
 * @async
 * @function postToken
 * 
 * @param {Object} params - Parámetros de la petición
 * @param {string} params.user_id - ID único de sesión del usuario (generado localmente)
 * 
 * @returns {Promise<Object>} Objeto con token y código de estado
 * @returns {Promise<{data: Object|Array, statusCode: number}>} Estructura de respuesta:
 *   - data: Objeto con token y metadatos o array vacío en caso de error
 *   - data.token: Token JWT para autenticación (cuando statusCode es 200)
 *   - data.expires_in: Tiempo de expiración en segundos (cuando statusCode es 200)
 *   - statusCode: Código HTTP de respuesta (200, 401, 404, 500)
 * 
 * @throws {Object} Retorna objeto con statusCode 500 en caso de error de red
 * 
 * @example
 * // Generar token básico
 * const sessionId = generateSessionId();
 * const result = await postToken({ user_id: sessionId });
 * 
 * if (result.statusCode === 200) {
 *   const token = result.data.token;
 *   localStorage.setItem('sessionToken', token);
 *   console.log('Token generado:', token);
 * }
 * 
 * @example
 * // Manejo completo con reintentos
 * const generateAuthToken = async (sessionId, retryCount = 0) => {
 *   const result = await postToken({ user_id: sessionId });
 *   
 *   switch (result.statusCode) {
 *     case 200:
 *       localStorage.setItem('sessionToken', result.data.token);
 *       return result.data.token;
 *     case 401:
 *     case 404:
 *       if (retryCount < 3) {
 *         return generateAuthToken(sessionId, retryCount + 1);
 *       }
 *       throw new Error('No se pudo generar el token');
 *     case 500:
 *       throw new Error('Error del servidor');
 *     default:
 *       throw new Error(`Error desconocido: ${result.statusCode}`);
 *   }
 * };
 * 
 * @since 1.0.0
 * @author Valeria Garcia
 * 
 * @see {@link ../constantes/constantes.js} Para configuración de API_URL
 * @see {@link ../functions/functions.js} Para generateSessionId()
 * @see {@link ./getSearch.js} Servicio que consume el token generado
 */
export async function postToken({ user_id }) {
  // Validación de parámetros
  if (!user_id) {
    return { statusCode: 400, data: [] };
  }

  const apiURL = `${API_URL}generate-token`;
  const requestData = { user_id };

  const config = {
    method: 'POST',
    url: apiURL,
    timeout: 25000,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json; charset=utf-8'
    },
    data: requestData
  };

  try {
    const response = await axios(config);

    // Mapeo de códigos de respuesta
    const statusHandlers = {
      200: () => ({ data: response.data, statusCode: 200 }),
      401: () => ({ statusCode: 401, data: [] }), // No autorizado
      404: () => ({ statusCode: 404, data: [] })  // Endpoint no encontrado
    };

    const handler = statusHandlers[response.status];
    return handler ? handler() : { statusCode: response.status, data: [] };
    
  } catch (error) {
    // Manejo de errores de red/timeout
    if (error.response) {
      return { statusCode: error.response.status, data: [] };
    }
    
    // Error de red o timeout
    return { statusCode: 500, data: [] };
  }
}